import styled, { css } from 'styled-components';
import { Swiper, SwiperSlide as BaseSwiperSlide } from 'swiper/react';
import type { SwiperOptions } from 'swiper/types/swiper-options';

import { LegacyButton as BaseButton } from '@/components/LegacyButton';
import { ArticlePreview as originArticlePreview } from '@/views/NewsView/components/Article';

import {
  calcSwiperSlideBetweenByBreakpoints,
  calcSwiperSlideMaxWidth,
  calcSwiperSlideMaxWidthByBreakpoints,
  calcSwiperSlideSpaceBetween,
} from '../Grid/helper';

import { ReactComponent as ChevronSvg } from '@/assets/icons/chevron.svg';
export const Wrapper = styled.div`
  padding-bottom: ${({ theme }) => theme.spacing(4)};

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    padding-bottom: ${({ theme }) => theme.spacing(8)};
  }
`;

export const GridContainer = styled.div`
  max-width: ${({ theme }) => theme.breakpoints.lg};
  margin: 0 auto;
  display: grid;
  gap: ${({ theme }) => theme.spacing(3)};
  grid-template-columns: repeat(4, 1fr);

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

export const Text = styled.div`
  text-transform: uppercase;
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  font-family: ${({ theme }) => theme.fontFamilies.italianPlate};
  font-size: ${({ theme }) => theme.fontSizes.h2};
  text-align: center;
`;

export const ArticlePreview = styled(originArticlePreview).attrs({
  infoClassName: 'info',
  cardClassName: 'card',
  imgClassName: 'img',
})<{ $single?: boolean }>`
  ${({ $single, theme }) => css`
    ${$single &&
    css`
      background: ${theme.colors.secondaryLightGrey};
      button {
        width: 100%;
      }
      ${theme.media.md.up} {
        button {
          width: unset;
        }
        .card {
          display: flex;
        }
        .img {
          flex: 2;
        }
        .info {
          flex: 1;
          justify-content: center;
          display: flex;
          flex-direction: column;

          padding: ${theme.spacing(0, 2.5, 2.5, 2.5)};
        }
      }
    `}
    .info {
      padding: ${theme.spacing(0, 2.5, 2.5, 2.5)};
    }
  `}
`;

export const Chevron = styled(ChevronSvg)`
  width: 18px;
  height: 18px;
  fill: ${({ theme }) => theme.colors.primaryDark};
`;

export const Discover = styled(BaseButton)`
  text-transform: uppercase;
  text-decoration: underline;
  text-underline-offset: 5px;
  gap: 0;
  ${({ theme }) => css`
    text-decoration-color: ${theme.colors.primary};
    &:hover,
    &:focus {
      color: ${theme.colors.primaryDark};
    }
  `};
`;

export const Header = styled.div`
  margin-top: ${({ theme }) => theme.spacing(6)};
  margin-bottom: ${({ theme }) => theme.spacing(1)};
  max-width: ${({ theme }) => theme.breakpoints.lg};
  text-align: center;
`;

export const StyledSwiper = styled(Swiper)<{
  $hideNavigation: boolean;
  breakpoints: SwiperOptions['breakpoints'];
  separator: string;
  slidesPerView: number | 'auto';
  spaceBetween?: string | number;
}>`
  .swiper-wrapper {
    .swiper-slide {
      height: auto;
      ${({ breakpoints, slidesPerView, spaceBetween }) => css`
        ${calcSwiperSlideMaxWidth({ slidesPerView, spaceBetween })}
        ${calcSwiperSlideMaxWidthByBreakpoints(breakpoints)}
        ${calcSwiperSlideSpaceBetween({ spaceBetween })}
        ${calcSwiperSlideBetweenByBreakpoints(breakpoints)}
      `}
    }
  }
  .swiper {
    ${({ $hideNavigation, theme }) => css`
      &-wrapper {
        padding-bottom: ${theme.spacing(2)};
        ${theme.media.md.up} {
          padding-bottom: ${theme.spacing(0)};
        }
      }

      ${$hideNavigation &&
      css`
        &-button-prev,
        &-button-next {
          display: none;
        }
      `}
      &-scrollbar {
        background: ${({ theme }) => theme.colors.primaryDark};
        height: 2px;
        bottom: 0;
        width: 100%;
        left: 0;
        ${theme.media.md.up} {
          display: none;
          width: 50%;
          left: 25%;
        }

        &-drag {
          background: ${({ theme }) => theme.colors.primary};
        }
      }
    `}
  }
`;

export const SwiperSlide = styled(BaseSwiperSlide)`
  ${({ theme }) => css`
    background: ${theme.colors.secondaryLightGrey};
  `}
`;
